import { resolveComboLinePrices } from '@tcr-erp/shared';
import { recalcItem } from './orderItemCalc';
import type { ComboSuggestion, PosComboDefinition } from './comboMatcher';
import type { FulfillmentMode, OrderItem } from '../types';

/** Sinh id dòng mới. Tiêm từ ngoài để test không phụ thuộc uuid ngẫu nhiên. */
export type IdFactory = () => string;

/** Số lượng của một variant đang nằm trong cụm. */
function qtyInGroup(items: OrderItem[], groupId: string, variantId: string): number {
  return items
    .filter((item) => item.comboGroupId === groupId && item.variantId === variantId)
    .reduce((acc, item) => acc + item.quantity, 0);
}

/** Số lượng của một variant đang là hàng lẻ (không thuộc cụm combo nào). */
function qtyLoose(items: OrderItem[], variantId: string): number {
  return items
    .filter((item) => !item.comboGroupId && item.variantId === variantId)
    .reduce((acc, item) => acc + item.quantity, 0);
}

/** Xoá sạch metadata combo và trả dòng về giá lẻ gốc. */
function toLooseLine(item: OrderItem, fulfillmentMode: FulfillmentMode): OrderItem {
  const loose: OrderItem = {
    ...item,
    price: item.retailPrice ?? item.price,
    discountPercent: 0,
    // Giữ lại cụm VỪA rời (transient, xem `OrderItem.splitComboGroupId`) —
    // màn sửa đơn cần nó để gửi `combo_group_id` (giá trị CŨ) kèm
    // `clear_combo:true` lúc lưu, cho BE tìm đúng dòng đã lưu (Task 13 fix
    // round 1, Finding 1). `item.comboGroupId` LUÔN có giá trị ở đây —
    // `toLooseLine` chỉ được gọi trên dòng đang thực sự thuộc cụm.
    splitComboGroupId: item.comboGroupId,
    comboGroupId: undefined,
    comboId: undefined,
    comboSku: undefined,
    comboName: undefined,
    comboUnitQty: undefined,
    comboUnitCount: undefined,
    comboUnitPrice: undefined,
    comboRetailTotal: undefined,
  };
  return recalcItem(loose, fulfillmentMode);
}

/**
 * Áp dụng một gợi ý combo lên giỏ hàng.
 *
 * Dòng bị dùng một phần được TÁCH ĐÔI: phần vào combo mang giá dòng, phần dư ở
 * lại làm dòng lẻ giá gốc. Giá lấy từ `resolveComboLinePrices` — đọc thẳng
 * `price_in_combo` đã chốt khi component đủ backfill, rơi về phân bổ theo
 * `combo_price` (cùng hàm BE dùng để chốt số) khi chưa — nên số hiển thị luôn
 * khớp số thu.
 *
 * `fulfillmentMode` phải truyền vào như các hàm anh em (`splitComboGroup`,
 * `setComboGroupCount`): `recalcItem` tính `totalAmount` khác nhau giữa INSTANT
 * và PREORDER, nên hard-code 'INSTANT' ở đây làm sai tổng dòng của giỏ PREORDER.
 */
export function applyComboSuggestion(
  items: OrderItem[],
  suggestion: ComboSuggestion,
  definition: PosComboDefinition,
  fulfillmentMode: FulfillmentMode,
  newId: IdFactory,
): OrderItem[] {
  const groupId = newId();
  const pricing = resolveComboLinePrices(
    definition.component_items,
    definition.combo_price,
    suggestion.unit_count,
  );
  const usedByItem = new Map(suggestion.uses.map((use) => [use.itemId, use.qty]));

  const result: OrderItem[] = [];
  for (const item of items) {
    const used = usedByItem.get(item.id);
    if (!used) {
      result.push(item);
      continue;
    }

    const component = definition.component_items.find((c) => c.variant_id === item.variantId);
    const priced = pricing.lines.find((line) => line.variant_id === item.variantId);
    if (!component || !priced) {
      result.push(item);
      continue;
    }

    const comboLine: OrderItem = recalcItem(
      {
        ...item,
        quantity: used,
        price: priced.unit_price,
        discountPercent: 0,
        retailPrice: item.retailPrice ?? item.price,
        comboGroupId: groupId,
        comboId: definition._id,
        comboSku: definition.combo_sku,
        comboName: definition.combo_name,
        comboUnitQty: component.qty,
        comboUnitCount: suggestion.unit_count,
        comboUnitPrice: pricing.effective_unit_price,
        comboRetailTotal: pricing.retail_unit_total,
        // Dòng vừa vào cụm MỚI — vết cụm cũ (nếu có, từ một lần tách trước đó)
        // hết ý nghĩa, không được rò rỉ sang lần lưu sau (Task 13 fix round 1,
        // Finding 1).
        splitComboGroupId: undefined,
      },
      fulfillmentMode,
    );
    result.push(comboLine);

    const leftover = item.quantity - used;
    if (leftover > 0) {
      result.push(
        recalcItem(
          {
            ...item,
            id: newId(),
            // Critical finding 2 (final review): dòng dư là một dòng VẬT LÝ
            // MỚI tách ra từ `item` — KHÔNG được mang cùng `lineId` với phần
            // đã vào combo, nếu không BE (khớp ưu tiên theo `line_id`) sẽ coi
            // hai entry payload này là CÙNG một dòng đã lưu và một entry ghi
            // đè âm thầm lên entry kia. `savedSku` (nếu có) KHÔNG bị xoá ở
            // đây — vẫn cần đi theo để BE còn khớp lại được qua
            // (sku, combo_group_id) một khi `lineId` đã mất.
            lineId: undefined,
            quantity: leftover,
            price: item.retailPrice ?? item.price,
            splitComboGroupId: undefined,
          },
          fulfillmentMode,
        ),
      );
    }
  }
  return result;
}

/** Tách cụm: mọi dòng của cụm trở về dòng lẻ giá gốc. */
export function splitComboGroup(
  items: OrderItem[],
  comboGroupId: string,
  fulfillmentMode: FulfillmentMode,
): OrderItem[] {
  return items.map((item) =>
    item.comboGroupId === comboGroupId ? toLooseLine(item, fulfillmentMode) : item,
  );
}

/** Xoá cả cụm khỏi giỏ. Dòng lẻ cùng variant không bị đụng. */
export function removeComboGroup(items: OrderItem[], comboGroupId: string): OrderItem[] {
  return items.filter((item) => item.comboGroupId !== comboGroupId);
}

/**
 * Số combo tối đa cụm có thể lên tới, tính từ hàng ĐANG trong cụm cộng hàng lẻ
 * cùng variant còn trong giỏ. KHÔNG tính hàng chưa có trong giỏ — stepper không
 * được tự mua hộ khách.
 */
export function maxComboCount(
  items: OrderItem[],
  comboGroupId: string,
  definition: PosComboDefinition,
): number {
  return definition.component_items.reduce((acc, component) => {
    const total =
      qtyInGroup(items, comboGroupId, component.variant_id) +
      qtyLoose(items, component.variant_id);
    return Math.min(acc, Math.floor(total / component.qty));
  }, Number.POSITIVE_INFINITY);
}

/**
 * Đặt lại số combo của cụm.
 *
 * Giảm → phần dôi ra thành dòng lẻ giá gốc. Về 0 tương đương tách cụm.
 * Tăng → rút hàng lẻ cùng variant vào cụm; không đủ thì NÉM LỖI chứ không tự
 * thêm hàng vào giỏ (thêm hàng là hành động riêng của thu ngân).
 */
export function setComboGroupCount(
  items: OrderItem[],
  comboGroupId: string,
  nextCount: number,
  definition: PosComboDefinition,
  fulfillmentMode: FulfillmentMode,
  newId: IdFactory,
): OrderItem[] {
  if (nextCount <= 0) return splitComboGroup(items, comboGroupId, fulfillmentMode);
  if (nextCount > maxComboCount(items, comboGroupId, definition)) {
    throw new Error('Không đủ hàng trong giỏ để tăng số combo');
  }

  const pricing = resolveComboLinePrices(
    definition.component_items,
    definition.combo_price,
    nextCount,
  );

  // Bước 1: gỡ cụm về dòng lẻ để gom lại từ đầu — đơn giản hơn nhiều so với
  // cộng/trừ tại chỗ, và không có nhánh nào bị bỏ sót.
  const loosened = splitComboGroup(items, comboGroupId, fulfillmentMode);

  // Bước 2: rút đúng số lượng cần cho `nextCount` combo, ưu tiên dòng đứng trước.
  const result: OrderItem[] = [];
  const need = new Map(
    definition.component_items.map((c) => [c.variant_id, c.qty * nextCount]),
  );

  for (const item of loosened) {
    const remaining = need.get(item.variantId) ?? 0;
    // Bỏ qua dòng đang thuộc cụm KHÁC (còn `comboGroupId` sau khi lọc `loosened`
    // chỉ gỡ đúng `comboGroupId` này) — không được rút hàng của cụm khác vào đây,
    // `maxComboCount` chỉ đếm hàng lẻ thật sự nên tập rút phải khớp tập đã đếm.
    if (remaining === 0 || item.comboGroupId) {
      result.push(item);
      continue;
    }

    const component = definition.component_items.find((c) => c.variant_id === item.variantId)!;
    const priced = pricing.lines.find((line) => line.variant_id === item.variantId)!;
    const take = Math.min(item.quantity, remaining);
    need.set(item.variantId, remaining - take);

    result.push(
      recalcItem(
        {
          ...item,
          quantity: take,
          price: priced.unit_price,
          discountPercent: 0,
          retailPrice: item.retailPrice ?? item.price,
          comboGroupId,
          comboId: definition._id,
          comboSku: definition.combo_sku,
          comboName: definition.combo_name,
          comboUnitQty: component.qty,
          comboUnitCount: nextCount,
          comboUnitPrice: pricing.effective_unit_price,
          comboRetailTotal: pricing.retail_unit_total,
          // Dòng vừa vào lại cụm (cùng cụm vừa gỡ ở bước 1, hoặc trước đó là
          // hàng lẻ) — vết `splitComboGroupId` cũ (nếu có) hết ý nghĩa, không
          // được gửi `clear_combo` cho một dòng vẫn còn trong combo (Task 13
          // fix round 1, Finding 1).
          splitComboGroupId: undefined,
        },
        fulfillmentMode,
      ),
    );

    const leftover = item.quantity - take;
    if (leftover > 0) {
      // `item` ở đây vừa được `splitComboGroup` (bước 1) gỡ khỏi CHÍNH cụm
      // đang đổi số lượng — `item.splitComboGroupId` đã mang đúng giá trị cụm
      // đó, giữ nguyên qua spread để phần dư (thực sự rời cụm) vẫn gửi được
      // `clear_combo` lúc lưu.
      //
      // Critical finding 2 (final review): NHƯNG `lineId` thì phải xoá tường
      // minh — phần dư này là dòng VẬT LÝ MỚI tách ra khỏi `item` (dòng ở lại
      // cụm, được dựng riêng ở trên, mới là dòng thật sự giữ nguyên
      // `lineId`). Không xoá thì hai entry payload cùng `lineId` khiến BE
      // khớp cả hai vào MỘT dòng đã lưu, ghi đè âm thầm. `savedSku` KHÔNG bị
      // xoá — vẫn đi theo qua spread để BE khớp lại qua
      // (sku, combo_group_id) + `clear_combo`.
      result.push(
        recalcItem({ ...item, id: newId(), lineId: undefined, quantity: leftover }, fulfillmentMode),
      );
    }
  }

  return result;
}
